import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import merge from "lodash/merge";
import React, { useEffect, useRef, useState } from "react";
import { css } from "styled-components";

const enum fields {
    style = "style",
    content = "content",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.style]: string;
        [fields.content]: string;
    };
    extendedStyles?: CodeSnippetStyles;
}

type Props = OwnProps & HasShellContext;

export interface CodeSnippetStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    instructionText?: TypographyPresentationProps;
}

export const codeSnippetStyles: CodeSnippetStyles = {
    wrapper: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
    titleText: {
        variant: "h6",
        css: css`
            margin: 0;
        `,
    },
};

const styleDefaultValue = `
<style type="text/css">
</style>
`;

const scriptDefaultValue = `<div id="hubspot-form-container"></div>
<script charset="utf-8" type="text/javascript">
    window.loadScriptHubspotScript().then(() => {
        hbspt.forms.create({
            region: "...",                      // Here goes the region
            portalId: "...",                    // Here goes the portal id
            formId: "...",                      // Here goes the form id
            target: '#hubspot-form-container',  // Where the form is going to be added
        });
    });
</script>`;

const CodeSnippet = ({ fields: { style, content }, shellContext, extendedStyles, id }: Props) => {
    const [styles] = useState(() => merge(codeSnippetStyles, extendedStyles));
    const wrapperRef = useRef<HTMLDivElement>(null);
    const styleWrapperRef = useRef<HTMLDivElement>(null);
    const scriptWrapperRef = useRef<HTMLDivElement>(null);

    const scriptDefinition = `
<!--[if lte IE 8]>
<script charset="utf-8" type="text/javascript" src="//js.hsforms.net/forms/v2-legacy.js"></script>
<![endif]-->
<script charset="utf-8" type="text/javascript">
if (window.loadScriptHubspotScript == null) {
    window.loadScriptHubspotScript = () => {
        const script = document.createElement('script');
        script.src = '//js.hsforms.net/forms/v2.js';
        document.body.appendChild(script);
        return new Promise((res, rej) => {
            script.onload = function() {
                res();
            }
            script.onerror = function() {
                rej();
            }
        });
    }
}
</script>
`;

    useEffect(() => {
        if (styleWrapperRef.current) {
            const fragment = document.createRange().createContextualFragment(style);
            styleWrapperRef.current.innerHTML = "";
            styleWrapperRef.current.appendChild(fragment);
        }
        if (scriptWrapperRef.current) {
            const fragment = document.createRange().createContextualFragment(scriptDefinition);
            scriptWrapperRef.current.innerHTML = "";
            scriptWrapperRef.current.appendChild(fragment);
        }
        setTimeout(() => {
            if (wrapperRef.current) {
                const fragment = document.createRange().createContextualFragment(content);
                wrapperRef.current.innerHTML = "";
                wrapperRef.current.appendChild(fragment);
            }
        }, 50);
    }, []);

    return (
        <>
            {shellContext.isInShell && shellContext.isEditing && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.titleText}>{translate("Code Snippet")}</Typography>
                    <Typography {...styles.instructionText}>{siteMessage("CodeSnippet_Instruction")}</Typography>
                </StyledWrapper>
            )}
            {IS_SERVER_SIDE ? (
                <>
                    {/* eslint-disable react/no-danger */}
                    <div className="code-snippet__style" dangerouslySetInnerHTML={{ __html: fields.style }}></div>
                    <div
                        className="code-snippet__definition"
                        dangerouslySetInnerHTML={{ __html: scriptDefinition }}
                    ></div>
                    <div className="code-snippet__snippet" dangerouslySetInnerHTML={{ __html: fields.content }}></div>
                    {/* eslint-enable react/no-danger */}
                </>
            ) : (
                <>
                    <div
                        id={`snippet-stype-wrapper_${id}`}
                        className="code-snippet__snippet"
                        ref={styleWrapperRef}
                    ></div>
                    <div
                        id={`snippet-deinition-wrapper_${id}`}
                        className="code-snippet__snippet"
                        ref={scriptWrapperRef}
                    ></div>
                    <div id={`snippet-wrapper_${id}`} className="code-snippet__snippet" ref={wrapperRef}></div>
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(CodeSnippet),
    definition: {
        group: "Basic",
        canAdd: context => context.permissions?.canUseAdvancedFeatures || false,
        fieldDefinitions: [
            {
                name: fields.style,
                displayName: "Style",
                tooltip: "",
                editorTemplate: "CodeSnippetField",
                defaultValue: styleDefaultValue,
                fieldType: "Contextual",
            },
            {
                name: fields.content,
                displayName: "Code",
                tooltip: "",
                editorTemplate: "CodeSnippetField",
                defaultValue: scriptDefaultValue,
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
